import { EMPTY_VALUE } from 'src/app/_helpers/utils';
import { InlineBadgeVariant } from 'src/app/shared/components/inline-badge/inline-badge.component';

import { APIResponse, SortDirection } from './api-responses.types';

export { SortDirection };

export type InsurerDto = {
  insurerId: string;
  name: string;
  nameInFrench: string;
};

export type BillType =
  | 'PREMIUM_NOTICE'
  | 'SURPLUS'
  | 'DEFINITIVE_TALLY'
  | 'OTHER';
export type ReminderType = 'PAYMENT_REMINDER' | 'PAYMENT_SUMMONS';
export type BillingItemKind = 'INVOICE' | 'REMINDER';
export type BillingDocumentType = BillType | ReminderType;
export type BillState = 'PENDING' | 'TO_BE_VALIDATED' | 'REJECTED' | 'SENT';
export type BillSource = 'MANUAL' | 'ECOHUB';

export type InvoiceSort =
  | 'CUSTOMER_NAME'
  | 'POLICY_ID'
  | 'BILL_TYPE'
  | 'INSURER_NAME'
  | 'RECEPTION_DATE'
  | 'INVOICE_DATE'
  | 'PERIOD_START_DATE'
  | 'AMOUNT'
  | 'PREMIUM'
  | 'SOURCE'
  | 'INTERNAL_STATUS'
  | 'PREMIUM_GAP';

export function getBillTypeLabel(type: BillType): string {
  switch (type) {
    case 'PREMIUM_NOTICE':
      return $localize`Avis de prime`;
    case 'DEFINITIVE_TALLY':
      return $localize`Décompte définitif`;
    case 'SURPLUS':
      return $localize`Part excédent`;
    case 'OTHER':
      return $localize`Autre`;
  }
}

export const BILL_TYPES: BillType[] = [
  'PREMIUM_NOTICE',
  'SURPLUS',
  'DEFINITIVE_TALLY',
  'OTHER',
];

export const REMINDER_TYPES: ReminderType[] = [
  'PAYMENT_REMINDER',
  'PAYMENT_SUMMONS',
];

export const BILLING_DOCUMENT_TYPES: BillingDocumentType[] = [
  ...BILL_TYPES,
  ...REMINDER_TYPES,
];

export function isBillType(type: BillingDocumentType): type is BillType {
  return (BILL_TYPES as BillingDocumentType[]).includes(type);
}

export function isReminderType(
  type: BillingDocumentType
): type is ReminderType {
  return (REMINDER_TYPES as BillingDocumentType[]).includes(type);
}

export function getReminderTypeLabel(type: ReminderType): string {
  switch (type) {
    case 'PAYMENT_REMINDER':
      return $localize`Rappel de paiement`;
    case 'PAYMENT_SUMMONS':
      return $localize`Sommation de paiement`;
  }
}

export function getBillingDocumentTypeLabel(type: BillingDocumentType): string {
  return isReminderType(type)
    ? getReminderTypeLabel(type)
    : getBillTypeLabel(type);
}

export function getBillTypeColor(type: BillType): string {
  switch (type) {
    case 'PREMIUM_NOTICE':
      return '#2563eb';
    case 'SURPLUS':
      return '#8b5cf6';
    case 'DEFINITIVE_TALLY':
      return '#16a34a';
    case 'OTHER':
      return '#6b7280';
  }
}

export function getBillingDocumentTypeColor(type: BillingDocumentType): string {
  return isReminderType(type) ? 'var(--warning-600)' : getBillTypeColor(type);
}

export function getBillStateLabel(state: BillState): string {
  switch (state) {
    case 'TO_BE_VALIDATED':
      return $localize`À traiter`;
    case 'PENDING':
      return $localize`En attente`;
    case 'SENT':
      return $localize`Transmise`;
    case 'REJECTED':
      return $localize`Refusée`;
  }
}

/** Badge colour paired with {@link getBillStateLabel}; kept here as the single source of truth. */
export function getBillStateVariant(state: BillState): InlineBadgeVariant {
  switch (state) {
    case 'TO_BE_VALIDATED':
      return 'warning';
    case 'PENDING':
      return 'blue';
    case 'SENT':
      return 'success';
    case 'REJECTED':
      return 'error';
  }
}

export type CreateInvoiceRequest = {
  amount: number;
  type: BillType;
  status: BillState;
  proposalId: string | null;
  policyId: string | null;
  receptionDate: string;
  startDate: string;
  endDate: string;
  billingDate: string;
  file?: File;
};

export type CreateInvoiceResponse = APIResponse<{ id: string }>;

export type CreateReminderRequest = {
  customerId: string;
  proposalId: string;
  originalInvoiceId?: string;
  type: ReminderType;
  amount: number;
  receptionDate: string;
  billingDate: string;
  startDate?: string;
  endDate?: string;
  file: File;
};

export type CreateReminderResponse = APIResponse<{ id: string }>;

export type BillingImportRequest =
  | {
      kind: 'INVOICE';
      payload: CreateInvoiceRequest;
    }
  | {
      kind: 'REMINDER';
      payload: CreateReminderRequest;
    };

/** Payload for PUT /invoice/{invoiceId} — same fields as create minus the status. */
export type UpdateInvoiceRequest = {
  amount: number;
  type: BillType;
  proposalId: string | null;
  policyId: string | null;
  receptionDate: string;
  startDate: string;
  endDate: string;
  billingDate: string;
  file?: File;
};

/** Payload for PUT /reminder/{reminderId}. */
export type UpdateReminderRequest = Omit<CreateReminderRequest, 'file'> & {
  file?: File;
};

export type BillingUpdateRequest =
  | { kind: 'INVOICE'; payload: UpdateInvoiceRequest }
  | { kind: 'REMINDER'; payload: UpdateReminderRequest };

export type InvoiceInsightsCustomer = {
  userId: string;
  familyMemberId: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  /** How the main member of the customer account wants to receive bills. */
  communicationMethod?: DeliveryMethod | null;
};

export type InvoiceCustomerCoordinates = {
  email: string;
  address: string;
  postCode: string;
  city: string;
  /** ISO code. */
  country?: string | null;
};

export type InvoiceInsightsPolicy = {
  proposalState?: string;
  insuranceType?: string;
  offerId?: string;
  policyId: string;
  policyStatus?: string;
  startDate?: string;
  endDate?: string;
  periodicity?: string;
  premiumAmount?: number;
  totalAmount?: number;
  partner?: { id: string; name: string };
  agent?: { id: string; name: string };
  category?: {
    categoryId: string;
    categoryName: string;
    categoryNameInFrench: string;
    categoryLogo: string;
  };
};

export type BillingItemInsight = {
  id: string;
  displayId?: string | null;
  kind: BillingItemKind;
  originalInvoiceId?: string | null;
  originalInvoiceDisplayId?: string | null;
  invoiceNo?: string | null;
  amount: number;
  premiumGap?: number | null;
  premiumGapPercentage?: number | null;
  type?: BillingDocumentType | null;
  status: BillState;
  source: BillSource;
  customer?: InvoiceInsightsCustomer | null;
  policy?: InvoiceInsightsPolicy | null;
  ecohubEventId?: string | null;
  receptionDate: string;
  startDate?: string | null;
  endDate?: string | null;
  billingDate: string;
};

export type InvoiceBillingItemInsight = BillingItemInsight & {
  kind: 'INVOICE';
  type?: BillType | null;
};

export type ReminderBillingItemInsight = BillingItemInsight & {
  kind: 'REMINDER';
  type?: ReminderType | null;
  reminderType?: ReminderType | null;
};

export function isInvoiceBillingItem(
  item: BillingItemInsight
): item is InvoiceBillingItemInsight {
  return item.kind === 'INVOICE';
}

export function isReminderBillingItem(
  item: BillingItemInsight
): item is ReminderBillingItemInsight {
  return item.kind === 'REMINDER';
}

/**
 * Minimal shape needed to resolve a row's document type. Structural rather than
 * tied to `BillingItemInsight` so the flow-invoice rows can use it too.
 */
type BillingDocumentTypeSource = {
  kind: BillingItemKind;
  type?: BillingDocumentType | null;
  reminderType?: ReminderType | null;
};

export function getBillingItemDocumentType(
  item: BillingDocumentTypeSource
): BillingDocumentType | null {
  if (item.kind === 'REMINDER') {
    return item.type ?? item.reminderType ?? null;
  }
  return item.type ?? null;
}

export function invoiceGetPremium(invoice: BillingItemInsight): number | null {
  if (!isInvoiceBillingItem(invoice)) return null;
  return invoice.policy?.premiumAmount ?? null;
}

/** Whether a policy is attached; required before the invoice can be sent. */
export function invoiceHasPolicy(invoice: BillingItemInsight): boolean {
  return isInvoiceBillingItem(invoice) && invoice.policy?.policyId != null;
}

export function invoiceGetGap(
  invoice: BillingItemInsight
): { amount: number; pct: number } | null {
  if (!isInvoiceBillingItem(invoice)) return null;
  if (invoice.premiumGap == null || invoice.premiumGapPercentage == null)
    return null;
  return {
    amount: invoice.premiumGap,
    pct: invoice.premiumGapPercentage * 100,
  };
}

/** Joins a first/last name into a trimmed full name, or '' when both are blank. */
function joinName(firstName?: string | null, lastName?: string | null): string {
  return `${firstName ?? ''} ${lastName ?? ''}`.trim();
}

export function invoiceGetCustomerFullName(
  invoice: BillingItemInsight
): string | null {
  const c = invoice.customer;
  if (!c) return null;
  return (
    invoice.customer?.companyName || joinName(c.firstName, c.lastName) || null
  );
}

export type InvoicePageRequest = {
  status?: BillState[];
  kind?: BillingItemKind;
  categoryId?: string;
  receptionDateFrom?: string;
  receptionDateTo?: string;
  search?: string;
  invoiceType?: BillType;
  date?: string;
  pageSize: number;
  pageCount: number;
  sortBy?: InvoiceSort;
  sortDir?: SortDirection;
};

/** Same filters as the list page — the CSV export always covers every matching row. */
export type InvoiceExportRequest = Omit<
  InvoicePageRequest,
  'pageSize' | 'pageCount'
>;

export type InvoicePageResponse = APIResponse<{
  items: BillingItemInsight[];
  total: number;
}>;

/** Polymorphic invoice emitter, discriminated by `type`. */
export type InvoiceEmitter = { companyName: string };

/** Presigned S3 link to a document. The URL is temporary and expires. */
export type InvoiceDocument = {
  url: string;
  filename: string | null;
};

/**
 * Full detail of a single invoice (GET /invoice/{invoiceId}).
 *
 * NB: `document.url` and `policyDocument.url` are presigned S3 links that
 * expire — refetch the detail to obtain fresh links rather than caching them.
 * `emitter`, `document`, `policyDocument` and `proposalId` are independently
 * nullable; render defensively.
 */
export type InvoiceDetail = {
  id: string;
  displayId?: string | null;
  amount: number;
  type: BillType | null;
  status: BillState;
  source: BillSource;
  emitter: InvoiceEmitter | null;
  document: InvoiceDocument | null;
  policyDocument: InvoiceDocument | null;
  proposalId: string | null;
  receptionDate: string;
  startDate: string;
  endDate: string;
  billingDate: string;
  invoiceNo?: string | null;
  communicationMethod: DeliveryMethod | null;
  customer?: InvoiceInsightsCustomer | null;
  customerCoordinates: InvoiceCustomerCoordinates | null;
  /** The recorded status-change decision; null while still TO_BE_VALIDATED. */
  statusChange: InvoiceStatusChange | null;
};

export type InvoiceDetailResponse = APIResponse<InvoiceDetail>;

export type AttachCustomerToInvoiceRequest = { customerId: string };
export type AttachPolicyToInvoiceRequest = { policyId: string };
export type UpdateInvoiceTypeRequest = { type: BillType };

/** Reason explaining a gap between the invoiced amount and the policy premium. */
export type InvoiceGapReason =
  | 'AUTO_INDEXATION'
  | 'RATE_INCREASE'
  | 'INPUT_ERROR'
  | 'FINAL_PREMIUM_STATEMENT'
  | 'SURPLUS_SHARING'
  | 'OTHER';

/** How a validated invoice should be delivered to the customer. */
export type DeliveryMethod = 'EMAIL' | 'POST';

/** Admin who performed an invoice status transition. */
export type InvoiceActor = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
};

/**
 * The recorded status-change decision (GET /invoice/{invoiceId}.statusChange).
 * Present once the invoice has been processed; null while still TO_BE_VALIDATED.
 */
export type InvoiceStatusChange = {
  gapReason: InvoiceGapReason | null;
  comment: string | null;
  changedBy: InvoiceActor;
  changedAt: string;
};

/** Full name of the acting admin, falling back to their email. */
export function getInvoiceActorName(actor: InvoiceActor): string {
  return joinName(actor.firstName, actor.lastName) || actor.email;
}

/**
 * Body of PATCH /invoice/{invoiceId}/process. Every field is optional: when
 * `setStatus` is omitted the backend derives the outcome (SENT/PENDING) from
 * `gapReason`; `setStatus` is only honoured together with gapReason OTHER (or
 * none). `communicationMethod` determines how a sent invoice reaches the
 * customer.
 */
export type ProcessInvoiceRequest = {
  setStatus?: Extract<BillState, 'SENT' | 'PENDING' | 'REJECTED'>;
  comment?: string;
  /** Rich-text body sent for an OTHER decision or pending-invoice validation. */
  notificationContent?: string;
  gapReason?: InvoiceGapReason;
  communicationMethod?: DeliveryMethod;
};

export type InvoiceReference = {
  id: string;
  displayId?: string | null;
  invoiceNo?: string | null;
  amount: number;
  type?: BillType | null;
  status: BillState;
  billingDate: string;
  startDate: string;
  endDate: string;
};

export type SearchProposalInvoicesResponse = APIResponse<{
  invoices: InvoiceReference[];
}>;

export type ReminderOriginalInvoice = InvoiceDetail & {
  customer?: InvoiceInsightsCustomer | null;
  policy?: InvoiceInsightsPolicy | null;
};

export type ReminderDetails = {
  id: string;
  displayId?: string | null;
  type: ReminderType;
  originalInvoiceId?: string | null;
  invoiceNo?: string | null;
  invoiceAmount: number;
  amount: number;
  currency?: string | null;
  reminderFee?: number | null;
  status: BillState;
  source: BillSource;
  customer?: InvoiceInsightsCustomer | null;
  policy?: InvoiceInsightsPolicy | null;
  receptionDate: string;
  startDate?: string | null;
  endDate?: string | null;
  billingDate: string;
  interruptionDate?: string | null;
  document?: InvoiceDocument | null;
  /** Presigned link to the policy's own document; independent of any linked invoice. */
  policyDocument?: InvoiceDocument | null;
  originalInvoice?: ReminderOriginalInvoice | null;
  communicationMethod?: DeliveryMethod | null;
  statusChange?: InvoiceStatusChange | null;
  processable: boolean;
};

export type ReminderDetailResponse = APIResponse<ReminderDetails>;

export type ProcessReminderRequest = {
  setStatus: Extract<BillState, 'PENDING' | 'SENT' | 'REJECTED'>;
  comment?: string;
  communicationMethod?: DeliveryMethod;
};

/**
 * Above this gap ratio (10%) the backend holds a RATE_INCREASE invoice instead
 * of validating it. Mirrored here to phrase the mismatch button and to split the
 * RATE_INCREASE reason label into its below/above variants — the outcome is
 * still decided server-side.
 */
export const RATE_INCREASE_HOLD_THRESHOLD = 0.1;

export function getInvoiceGapReasonLabel(reason: InvoiceGapReason): string {
  switch (reason) {
    case 'AUTO_INDEXATION':
      return $localize`Indexation automatique`;
    case 'RATE_INCREASE':
      return $localize`Augmentation tarifaire`;
    case 'INPUT_ERROR':
      return $localize`Erreur de saisie`;
    case 'FINAL_PREMIUM_STATEMENT':
      return $localize`Décompte de prime définitif`;
    case 'SURPLUS_SHARING':
      return $localize`Participation aux excédents`;
    case 'OTHER':
      return $localize`Autre`;
  }
}

/** One invoice row of GET /insurance-flow/{flowId}/invoices. */
export type FlowInvoiceInsight = {
  id: string;
  insuranceFlowId: string;
  flowDisplayId: string;
  proposalId: string;
  kind: BillingItemKind;
  displayId?: string | null;
  invoiceNo?: string | null;
  /** Empty on reminders — read through {@link getBillingItemDocumentType}. */
  type?: BillType | null;
  /** Empty on invoices. */
  reminderType?: ReminderType | null;
  /** The invoice a reminder chases; absent on invoices. */
  originalInvoiceId?: string | null;
  originalInvoiceDisplayId?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  receptionDate: string;
  billingDate: string;
  amount: number;
  policyPremium?: number | null;
  premiumGap?: number | null;
  premiumGapPercentage?: number | null;
  gapReason?: InvoiceGapReason | null;
  status: BillState;
  source: BillSource;
  communicationMethod?: DeliveryMethod | null;
  processedBy?: InvoiceActor | null;
  hasDocument: boolean;
  documentFilename?: string | null;
};

/** Unpaginated: `total` always equals `items.length`. */
export type ListFlowInvoicesResponse = {
  items: FlowInvoiceInsight[];
  total: number;
};

export type FlowInvoicesFilters = {
  search?: string;
  categoryId?: string;
  status?: BillState[];
  /** Omitted returns both kinds; no control sets it today. */
  kind?: BillingItemKind;
  billingDate?: string;
  periodFrom?: string;
  periodTo?: string;
};

/**
 * Signed gap of a flow invoice, with the percentage scaled to 0-100. Always
 * null for a reminder, which has no LTY premium to differ from.
 */
export function flowInvoiceGetGap(
  invoice: FlowInvoiceInsight
): { amount: number; pct: number } | null {
  if (invoice.kind === 'REMINDER') return null;
  if (invoice.premiumGap == null || invoice.premiumGapPercentage == null)
    return null;
  return {
    amount: invoice.premiumGap,
    pct: invoice.premiumGapPercentage * 100,
  };
}

/** Colour role of the "Motif" cell; mirrors the gap colours. */
export type FlowInvoiceReasonTone =
  | 'neutral'
  | 'positive'
  | 'negative'
  | 'info';

/**
 * The "Motif" cell: the recorded gap reason when there is one, otherwise a
 * value derived from the amounts (a credit note reads as a refund, and an
 * invoice matching its policy premium reads as concordant).
 */
export function getFlowInvoiceReason(invoice: FlowInvoiceInsight): {
  label: string;
  tone: FlowInvoiceReasonTone;
} {
  // A reminder carries no gap, so none of the derivations below apply — and the
  // "Concordant" branch would otherwise match it, since its premiumGap is empty.
  if (invoice.kind === 'REMINDER') {
    return { label: EMPTY_VALUE, tone: 'neutral' };
  }

  const { gapReason, premiumGap, premiumGapPercentage } = invoice;

  if (gapReason === 'AUTO_INDEXATION') {
    return { label: getInvoiceGapReasonLabel(gapReason), tone: 'info' };
  }

  if (gapReason === 'RATE_INCREASE') {
    const above =
      Math.abs(premiumGapPercentage ?? 0) > RATE_INCREASE_HOLD_THRESHOLD;
    return {
      label: above
        ? $localize`Augmentation tarif > 10%`
        : $localize`Augmentation tarif < 10%`,
      tone: 'negative',
    };
  }

  if (gapReason) {
    return {
      label: getInvoiceGapReasonLabel(gapReason),
      tone: gapTone(premiumGap),
    };
  }

  if (invoice.amount < 0) {
    return { label: $localize`Remboursement`, tone: 'positive' };
  }

  if (invoice.policyPremium != null && Math.abs(premiumGap ?? 0) < 0.01) {
    return { label: $localize`Concordant`, tone: 'neutral' };
  }

  return { label: EMPTY_VALUE, tone: 'neutral' };
}

function gapTone(gap?: number | null): FlowInvoiceReasonTone {
  if (gap == null || Math.abs(gap) < 0.01) return 'neutral';
  return gap > 0 ? 'negative' : 'positive';
}
